// Tenantra Inc. - Owner-only waitlist manager: view signups, send launch invites.
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Mail, Trash2, Rocket, Users, Send, CheckCircle2, Clock } from 'lucide-react';
import api, { apiHelpers } from '@/services/api';

const STATUS_STYLES = {
  waiting: 'bg-slate-100 text-slate-600',
  invited: 'bg-amber-100 text-amber-700',
  activated: 'bg-emerald-100 text-emerald-700',
};
const roleLabel = { owner: 'Landlord', property_manager: 'Property Mgr' };

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-4.5 h-4.5" /></div>
      <div>
        <div className="text-xl font-bold text-slate-900 leading-none">{value}</div>
        <div className="text-[11px] text-slate-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function WaitlistPanel() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ waiting: 0, invited: 0, activated: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/waitlist');
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (e) { if (e?.response?.status === 403) setDenied(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setBusy = (id, v) => setRowBusy((b) => ({ ...b, [id]: v }));

  const inviteAll = async () => {
    const pending = counts.waiting + counts.invited;
    if (!pending) { toast.message('No one left to invite'); return; }
    if (!window.confirm(`Send a launch invite email to ${pending} ${pending === 1 ? 'person' : 'people'}? Each gets a one-click activation link.`)) return;
    setBulkBusy(true);
    try { const r = await api.post('/admin/waitlist/invite', {}); toast.success(`Sent ${r.data.sent} invite${r.data.sent === 1 ? '' : 's'} ✓`); load(); }
    catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBulkBusy(false); }
  };

  const inviteOne = async (id) => {
    setBusy(id, true);
    try { await api.post(`/admin/waitlist/${id}/invite`); toast.success('Invite sent ✓'); load(); }
    catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(id, false); }
  };

  const remove = async (id, email) => {
    if (!window.confirm(`Remove ${email} from the waitlist?`)) return;
    setBusy(id, true);
    try { await api.delete(`/admin/waitlist/${id}`); toast.success('Removed'); load(); }
    catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(id, false); }
  };

  if (denied) return <div className="p-6"><p className="text-slate-600">Only the owner admin can manage the waitlist.</p></div>;

  return (
    <div className="space-y-6" data-testid="waitlist-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Launch waitlist</h1>
          <p className="text-sm text-slate-500">People who signed up from your campaign. Send launch invites when you're ready — each gets a one-click activation link with their details pre-filled.</p>
        </div>
        <button disabled={bulkBusy || loading} onClick={inviteAll} className="h-10 px-4 rounded-lg bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white text-sm font-medium inline-flex items-center gap-2" data-testid="waitlist-invite-all">
          {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}Send launch invites
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={Users} label="Total signups" value={counts.total || 0} color="bg-slate-100 text-slate-600" />
        <Stat icon={Clock} label="Waiting" value={counts.waiting || 0} color="bg-slate-100 text-slate-500" />
        <Stat icon={Send} label="Invited" value={counts.invited || 0} color="bg-amber-100 text-amber-600" />
        <Stat icon={CheckCircle2} label="Activated" value={counts.activated || 0} color="bg-emerald-100 text-emerald-600" />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Users className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No waitlist signups yet. Share your <span className="font-medium text-slate-700">/waitlist</span> link in the Facebook ad to start collecting them.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-2.5 font-semibold">Name</th>
                  <th className="px-4 py-2.5 font-semibold">Email</th>
                  <th className="px-4 py-2.5 font-semibold hidden sm:table-cell">Role</th>
                  <th className="px-4 py-2.5 font-semibold hidden md:table-cell">Province</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((e) => (
                  <tr key={e.id} data-testid={`waitlist-row-${e.id}`}>
                    <td className="px-4 py-3 text-slate-800 font-medium whitespace-nowrap">{e.full_name}</td>
                    <td className="px-4 py-3 text-slate-600">{e.email}</td>
                    <td className="px-4 py-3 text-slate-600 hidden sm:table-cell">{roleLabel[e.role_type] || e.role_type}</td>
                    <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{e.province || '—'}</td>
                    <td className="px-4 py-3"><span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[e.status] || 'bg-slate-100 text-slate-600'}`}>{e.status}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {e.status !== 'activated' && (
                          <button disabled={rowBusy[e.id]} onClick={() => inviteOne(e.id)} className="h-8 px-2.5 rounded-md border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`waitlist-invite-${e.id}`}>
                            {rowBusy[e.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}{e.status === 'invited' ? 'Resend' : 'Invite'}
                          </button>
                        )}
                        <button disabled={rowBusy[e.id]} onClick={() => remove(e.id, e.email)} className="h-8 px-2.5 rounded-md border border-red-200 text-xs text-red-600 hover:bg-red-50 inline-flex items-center gap-1" data-testid={`waitlist-remove-${e.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
